class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.modal = this.closest('.search-modal');
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.searchTerm = '';
    this.repositionPanel = this.positionPanel.bind(this);

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.querySelector('form.search').addEventListener('submit', this.onFormSubmit.bind(this));
    this.querySelector('button[type="button"]').addEventListener('click', this.close.bind(this));
    this.querySelector('button[type="reset"]').addEventListener('click', this.clear.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('keydown', this.onTabKey.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      // Begriff komplett gelöscht: Dropdown offen lassen und den Standard-Overlay
      // (beliebte Suchanfragen/Kategorien/Produkte) zeigen — nicht schließen.
      this.showDefaultState();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  showDefaultState() {
    // Zeigt den Empty-State (search-overlay-defaults) und hält das Dropdown offen.
    this.searchTerm = '';
    this.removeAttribute('results');
    if (this.predictiveSearchResults) this.predictiveSearchResults.innerHTML = '';
    this.open();
  }
  
  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      // Show default search overlay (popular searches, etc.) even with empty input
      this.open();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      // Eingabe leer (z. B. alles weggelöscht): Dropdown offen lassen, Standard-Overlay zeigen
      this.showDefaultState();
      return;
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  onTabKey(event) {
    if (!this.getAttribute('open')) return;
    if (event.key !== 'Tab') return;
    const allLinks = Array.from(this.querySelectorAll('li[role="option"] a, li[role="option"] button'));
    if (!allLinks.length) return;
    const currentIndex = allLinks.indexOf(document.activeElement);
    let nextIndex;
    if (event.shiftKey) {
      nextIndex = (currentIndex - 1 + allLinks.length) % allLinks.length;
    } else {
      nextIndex = (currentIndex + 1) % allLinks.length;
    }
    event.preventDefault();
    allLinks.forEach(link => link.setAttribute('tabindex', '-1'));
    allLinks[nextIndex].setAttribute('tabindex', '0');
    allLinks[nextIndex].focus();
    // Update aria-selected for the parent li
    allLinks.forEach(link => {
      const li = link.closest('li');
      if (li) li.setAttribute('aria-selected', 'false');
    });
    const activeLi = allLinks[nextIndex].closest('li');
    if (activeLi) activeLi.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', activeLi ? activeLi.id : '');
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
     
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      /*
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      */
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const moveUp = direction === 'up';
    // Index-basierte Navigation über alle Optionen (Kategorien + Produkte),
    // damit der Sprung über die Rubrik-Grenze (separate <ul>) funktioniert.
    const options = Array.from(this.querySelectorAll('li[role="option"]'));
    if (!options.length) return;

    const selectedElement = this.querySelector('li[role="option"][aria-selected="true"]');
    const currentIndex = selectedElement ? options.indexOf(selectedElement) : -1;
    if (moveUp && currentIndex === -1) return;

    let nextIndex;
    if (moveUp) {
      nextIndex = currentIndex <= 0 ? options.length - 1 : currentIndex - 1;
    } else {
      nextIndex = currentIndex >= options.length - 1 ? 0 : currentIndex + 1;
    }
    const activeElement = options[nextIndex];
    if (activeElement === selectedElement) return;

    if (this.statusElement) this.statusElement.textContent = '';
    options.forEach(li => {
      li.setAttribute('aria-selected', 'false');
      const link = li.querySelector('a, button');
      if (link) link.setAttribute('tabindex', '-1');
    });
    activeElement.setAttribute('aria-selected', 'true');
    const activeLink = activeElement.querySelector('a, button');
    if (activeLink) {
      activeLink.setAttribute('tabindex', '0');
      activeLink.focus();
    }
    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    // API-Maximum (10) anfragen, damit für die Kategorie-Sortierung nach
    // Sortimentsbreite möglichst alle matchenden Collections vorliegen.
    // Die angezeigte Produkt-/Kategorie-Anzahl wird in der Section (Liquid) begrenzt.
    const resultsLimit = 10;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      if (this.modal) this.modal.classList.add('searching');
      return;
    }

    fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,collection&${encodeURIComponent('resources[limit]')}=${resultsLimit}&${encodeURIComponent('resources[limit_scope]')}=each&${encodeURIComponent('resources[options][fields]')}=title,product_type,variants.title,vendor,tag&section_id=predictive-search`)
      .then((response) => { 
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;

        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        /*
        this.cachedResults[queryKey] = resultsMarkup;
        */
        this.renderSearchResults(resultsMarkup);
        
        if (this.modal) this.modal.classList.add('searching');
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        
        this.close();
        throw error;
      }); 
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);  

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() { 
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }
  
  /* Dropdown-Panel an der TATSAECHLICHEN Unterkante des Suchfelds ausrichten.

     Warum noetig: Das Panel ist `position: fixed; top: auto` (sections/header.liquid)
     und haengt damit an seiner *statischen* Layout-Position. Sobald der Header klebt,
     traegt er `.shopify-section-header-sticky` — und das ist `position: sticky`
     (assets/component-sticky-header.css:3, ueberschreibt das `fixed` aus dem Inline-Style
     des Headers, weil das Stylesheet spaeter geladen wird). Ein sticky-Element verschiebt
     sich aber nur VISUELL; seine Layout-Position bleibt im Dokumentfluss stehen.
     Die statische Position macht diese Verschiebung nicht mit → das Panel bleibt um die
     weggescrollte Hoehe (Announcement-Bar, 36 px) zu tief haengen. Gemessen bei scrollY=600:
     Suchfeld endet bei y=56, Panel startet bei y=92 → 18 px offener Spalt unter dem
     Header (Unterkante 74), durch den die Kategorieleiste durchscheint.

     getBoundingClientRect() liefert die visuelle Position inklusive Sticky-Verschiebung
     und ist damit die richtige Quelle. */
  positionPanel() {
    const panel = this.querySelector('.predictive-search--header');
    const field = this.querySelector('.header--cc__search-field') || this.querySelector('form.search');
    if (!panel || !field) return null;

    // Nur der fixed-Zustand braucht das. Absolut positionierte Panels haengen bereits
    // korrekt am mitscrollenden Wrapper (`top: calc(100% + 4px)`) — dort wuerde ein
    // Viewport-Wert das Panel verschieben.
    if (getComputedStyle(panel).position !== 'fixed') {
      panel.style.removeProperty('top');
      return null;
    }

    const top = Math.round(field.getBoundingClientRect().bottom);
    panel.style.top = `${top}px`;
    return top;
  }

  /* Nach dem Oeffnen zieht die Header-Geometrie noch nach, und zwar NACH dem letzten
     Event, das man abwarten koennte: Der Scroll-Lock zieht den Scroll auf 0 (Mobile),
     das scroll-Event feuert sofort — aber der StickyHeader raeumt seinen klebenden
     Zustand erst im daraufhin geplanten Frame ab. Gemessen auf 390x844:
       t=3ms  Suchfeld-Unterkante 27.5   (Header noch klebend/versteckt)
       t=4ms  scroll-Event         27.5
       t=11ms                     134.0  (StickyHeader.reset() ist durch)
     Ein einmaliges Messen — auch per einzelnem rAF — trifft also den veralteten Wert
     und das Panel stuende 106 px zu hoch ueber dem Suchfeld.
     Darum ein paar Frames nachfuehren, bis der Wert zwei Frames lang steht. Bricht
     typischerweise nach ~4 Frames ab, spaetestens nach 500 ms. */
  trackPanelPosition() {
    if (this._trackingPanel) return;
    this._trackingPanel = true;

    const start = performance.now();
    let last = null;
    let stableFrames = 0;

    const step = () => {
      if (!this.isOpen) { this._trackingPanel = false; return; }

      const top = this.positionPanel();
      stableFrames = (top !== null && top === last) ? stableFrames + 1 : 0;
      last = top;

      if (stableFrames < 2 && performance.now() - start < 500) {
        requestAnimationFrame(step);
      } else {
        this._trackingPanel = false;
      }
    };

    requestAnimationFrame(step);
  }

  getResultsMaxHeight() {
    const header = document.querySelector('.header--cc') || document.querySelector('.header');
    this.resultsMaxHeight = header
      ? window.visualViewport.height - header.getBoundingClientRect().bottom
      : window.visualViewport.height * 0.8;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    document.body.classList.add('predictive-search-open');
    this._ensureOverlay();

    // Nach dem Sichtbarmachen positionieren (vorher ist das Panel display:none).
    this.positionPanel();
    // ...und danach ein paar Frames nachfuehren, bis die Header-Geometrie steht
    // (siehe trackPanelPosition — der Scroll-Lock oben loest eine verzoegerte
    // Header-Bewegung aus, die kein weiteres Event mehr meldet).
    this.trackPanelPosition();
    // Header-Geometrie kann sich waehrend des offenen Zustands noch aendern
    // (Fenstergroesse, Sticky-Wechsel beim Scrollen auf Touch-Geraeten).
    // Gleiche Funktionsreferenz → mehrfaches open() registriert nicht doppelt.
    window.addEventListener('resize', this.repositionPanel, { passive: true });
    window.addEventListener('scroll', this.repositionPanel, { passive: true });
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.removeAttribute('results');
    this.input.setAttribute('aria-expanded', false);
    this.input.value = '';
    this.input.blur();
    if (this.modal) this.modal.classList.remove('searching');
    document.body.classList.remove('predictive-search-open');
    this._removeOverlay();

    window.removeEventListener('resize', this.repositionPanel);
    window.removeEventListener('scroll', this.repositionPanel);
    const panel = this.querySelector('.predictive-search--header');
    if (panel) panel.style.removeProperty('top');
  }

  _ensureOverlay() {
    if (document.querySelector('.predictive-search-overlay')) return;
    const overlay = document.createElement('div');
    overlay.className = 'predictive-search-overlay';
    overlay.addEventListener('click', () => this.close());
    document.body.appendChild(overlay);
  }

  _removeOverlay() {
    document.querySelector('.predictive-search-overlay')?.remove();
  }

  clear(event) {
    if (event) event.preventDefault();

    this.input.value = '';
    this.input.focus();
    // Eingabe gelöscht: Dropdown offen lassen und Standard-Overlay zeigen (statt zu schließen)
    this.showDefaultState();

    if (this.modal) this.modal.classList.remove('searching');
  }
}

customElements.define('predictive-search', PredictiveSearch);
